import { useMemo, useState } from 'react'
import { FileText, Plus, User } from 'lucide-react'
import type {
  CoverageLevel,
  CreatePersonaInput,
  MarketPersona,
  PersonaRole,
  PersonaWorkshopSession,
} from '@ispot/shared'
import type { useMarketSegmentation } from '@/hooks/useMarketSegmentation'
import { ImportPersonaDialog } from '@/components/persona-configurator/ImportPersonaDialog'
import { PersonaDialog } from '../personas/PersonaDialog'
import { PersonaList } from '../personas/PersonaList'
import { CoverageMatrix, nextCoverage } from '../personas/CoverageMatrix'

type HookState = ReturnType<typeof useMarketSegmentation>

interface Props {
  accountId: string
  projectId: string
  state: HookState
}

export function PersonasTab({ accountId, projectId, state }: Props) {
  const [dialog, setDialog] = useState<{ mode: 'create' } | { mode: 'edit'; persona: MarketPersona } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<MarketPersona | null>(null)
  const [showImport, setShowImport] = useState(false)

  const segmentCountByPersona = useMemo(() => {
    const m = new Map<string, number>()
    for (const c of state.coverage) {
      m.set(c.personaId, (m.get(c.personaId) ?? 0) + 1)
    }
    return m
  }, [state.coverage])

  const handleCycleCoverage = async (
    segmentId: string,
    personaId: string,
    current: CoverageLevel | null,
  ) => {
    const next = nextCoverage(current)
    try {
      if (next === null) {
        await state.unlinkPersona(segmentId, personaId)
      } else {
        await state.linkPersona(segmentId, personaId, next, null)
      }
    } catch {
      // Optimistic fallback is handled by the hook
    }
  }

  const handleSave = async (input: Parameters<HookState['createPersona']>[0]) => {
    if (dialog?.mode === 'edit') {
      await state.updatePersona(dialog.persona.id, input)
    } else {
      await state.createPersona(input)
    }
    setDialog(null)
  }

  const handleDelete = async () => {
    if (!confirmDelete) return
    await state.deletePersona(confirmDelete.id).catch(() => {})
    setConfirmDelete(null)
  }

  const handleImportCreated = async (session: PersonaWorkshopSession) => {
    await state.createPersona(workshopSessionToMarketPersona(session))
    await state.reload()
  }

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <header
        className="flex shrink-0 items-center justify-between border-b px-8 py-4"
        style={{ borderColor: 'var(--color-border-subtle)' }}
      >
        <div>
          <h2
            className="font-display text-sm font-semibold uppercase tracking-wider"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Personas
          </h2>
          <p
            className="mt-0.5 text-xs"
            style={{ color: 'var(--color-text-secondary)' }}
          >
            Cross-tabulate who's involved across segments. Click a cell to link a
            persona at primary/secondary/tertiary coverage.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setShowImport(true)}
            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold transition-colors hover:bg-[var(--color-warm-100)]"
            style={{
              borderColor: 'var(--color-border)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <FileText className="h-3.5 w-3.5" /> Import persona
          </button>
          <button
            type="button"
            onClick={() => setDialog({ mode: 'create' })}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-white"
            style={{ backgroundColor: 'var(--color-business, #0984e3)' }}
          >
            <Plus className="h-3.5 w-3.5" /> New persona
          </button>
        </div>
      </header>

      <div className="grid min-h-0 flex-1 grid-cols-[280px_1fr] gap-0">
        <PersonaList
          personas={state.personas}
          segmentCountByPersona={segmentCountByPersona}
          onEdit={(p) => setDialog({ mode: 'edit', persona: p })}
          onDelete={(p) => setConfirmDelete(p)}
          onAdd={() => setDialog({ mode: 'create' })}
        />

        <div className="overflow-auto p-6">
          {state.personas.length === 0 ? (
            <EmptyPersonas
              onAdd={() => setDialog({ mode: 'create' })}
              onImport={() => setShowImport(true)}
            />
          ) : state.segments.length === 0 ? (
            <EmptyMatrix />
          ) : (
            <CoverageMatrix
              segments={state.segments}
              personas={state.personas}
              coverage={state.coverage}
              beachheadId={state.canvas?.beachheadSegmentId ?? null}
              onCycle={handleCycleCoverage}
            />
          )}
        </div>
      </div>

      {dialog ? (
        <PersonaDialog
          persona={dialog.mode === 'edit' ? dialog.persona : null}
          onCancel={() => setDialog(null)}
          onSave={handleSave}
        />
      ) : null}

      {confirmDelete ? (
        <DeleteConfirmDialog
          persona={confirmDelete}
          onCancel={() => setConfirmDelete(null)}
          onConfirm={handleDelete}
        />
      ) : null}

      <ImportPersonaDialog
        accountId={accountId}
        projectId={projectId}
        open={showImport}
        onClose={() => setShowImport(false)}
        onCreated={handleImportCreated}
      />
    </div>
  )
}

function workshopSessionToMarketPersona(session: PersonaWorkshopSession): CreatePersonaInput {
  const data = session.personaData
  const name = data.name?.trim() || data.role?.trim() || 'Imported persona'
  return {
    name,
    role: mapPersonaRole(session),
    description: buildImportedPersonaDescription(session),
    linkedWorkshopPersonaId: session.id,
  }
}

function mapPersonaRole(session: PersonaWorkshopSession): PersonaRole {
  const role = session.personaData.role.toLowerCase()
  const decisionPower = session.personaData.decisionPower

  if (decisionPower === 'buyer' || session.personaType === 'buyer') return 'economic_buyer'
  if (decisionPower === 'influencer' || decisionPower === 'champion') return 'influencer'
  if (
    decisionPower === 'gatekeeper' ||
    role.includes('technical') ||
    role.includes('security') ||
    role.includes('it ') ||
    role.includes('procurement')
  ) {
    return 'technical_buyer'
  }
  return 'user'
}

function buildImportedPersonaDescription(session: PersonaWorkshopSession): string | null {
  const data = session.personaData
  const parts = [
    data.role ? `Role: ${data.role}` : null,
    data.archetype ? `Archetype: ${data.archetype}` : null,
    data.context ? `Context: ${data.context}` : null,
    data.jtbds?.[0]?.functional ? `JTBD: ${data.jtbds[0].functional}` : null,
    data.constraints?.length ? `Constraints: ${data.constraints.join(', ')}` : null,
  ].filter((part): part is string => Boolean(part))

  if (parts.length === 0) return null
  return parts.join('\n').slice(0, 2000)
}

// ============================================================================
// EMPTY STATES + DELETE CONFIRM
// ============================================================================

function EmptyPersonas({ onAdd, onImport }: { onAdd: () => void; onImport: () => void }) {
  return (
    <div
      className="flex h-full items-center justify-center rounded-lg border border-dashed py-16 text-center"
      style={{ borderColor: 'var(--color-border)' }}
    >
      <div className="max-w-sm">
        <User
          className="mx-auto mb-3 h-8 w-8"
          style={{ color: 'var(--color-business, #0984e3)' }}
        />
        <h3 className="font-display text-base font-semibold text-[var(--color-text)]">
          No personas yet
        </h3>
        <p
          className="mx-auto mt-1 text-xs"
          style={{ color: 'var(--color-text-secondary)' }}
        >
          Personas from the workshop, uploaded documents, or manual entry will
          appear here. Cross-tabulate them against your segments below.
        </p>
        <div className="mt-4 flex justify-center gap-2">
          <button
            type="button"
            onClick={onImport}
            className="inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-semibold"
            style={{
              borderColor: 'var(--color-border)',
              color: 'var(--color-text-secondary)',
            }}
          >
            <FileText className="h-3.5 w-3.5" /> Import persona
          </button>
          <button
            type="button"
            onClick={onAdd}
            className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold text-white"
            style={{ backgroundColor: 'var(--color-business, #0984e3)' }}
          >
            <Plus className="h-3.5 w-3.5" /> Add manually
          </button>
        </div>
      </div>
    </div>
  )
}

function EmptyMatrix() {
  return (
    <div
      className="flex h-full items-center justify-center rounded-lg border border-dashed py-16 text-center"
      style={{ borderColor: 'var(--color-border)' }}
    >
      <p className="max-w-sm text-xs" style={{ color: 'var(--color-text-secondary)' }}>
        Add segments on the Segments tab to build the coverage matrix.
      </p>
    </div>
  )
}

function DeleteConfirmDialog({
  persona,
  onCancel,
  onConfirm,
}: {
  persona: MarketPersona
  onCancel: () => void
  onConfirm: () => void
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/30"
      onClick={onCancel}
    >
      <div
        className="w-[420px] rounded-lg border p-5 shadow-lg"
        style={{
          backgroundColor: 'var(--color-bg, white)',
          borderColor: 'var(--color-border-subtle)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          className="font-display text-base font-semibold"
          style={{ color: 'var(--color-text)' }}
        >
          Delete persona?
        </h3>
        <p className="mt-2 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          <strong>{persona.name}</strong> will be removed from the canvas, along with
          any segment coverage links. This can't be undone.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md px-3 py-1.5 text-xs font-medium"
            style={{ color: 'var(--color-text-muted)' }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="rounded-md px-3 py-1.5 text-xs font-semibold text-white"
            style={{ backgroundColor: '#b14132' }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  )
}
